import { z } from 'zod'

const hex = z.string().regex(/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, 'must be a hex color')

/**
 * The entire authorable surface of a storefront. Branding, copy, and a
 * handful of commerce switches. No containers, no components, no
 * importRoute, no {{variable}} DSL.
 */
export const storefrontConfigSchema = z.object({
  name: z.string().min(1),
  storeName: z.string().min(1),
  logoUrl: z.string().url().or(z.literal('')),
  branding: z
    .object({
      primary: hex.optional(),
      secondary: hex.optional(),
      surface: hex.optional(),
      background: hex.optional(),
      mode: z.enum(['light', 'dark']).optional(),
      fontFamily: z.string().optional(),
      radius: z.number().int().min(0).max(64).optional(),
    })
    .default({}),
  commerce: z
    .object({
      currencySymbol: z.string().min(1).default('$'),
      locale: z.string().min(2).default('es-MX'),
      /**
       * The three below are `formatPrice`'s own knobs, surfaced through
       * `PresentationContext`. Left undefined so `formatPrice` applies its
       * defaults rather than this schema guessing them:
       *   currencyCode  -> its `code`    (e.g. 'MXN', rendered by {{code}})
       *   priceFormat   -> its `format`  (number-format.js mask, default '#,##0.00')
       *   priceDisplay  -> its `display` (default '{{symbol}}{{price}}{{code}}')
       */
      currencyCode: z.string().optional(),
      priceFormat: z.string().optional(),
      priceDisplay: z.string().optional(),
      /**
       * number-format.js mask for the discount percentages the promotion
       * engine prints (`'#0%'` in cinnamon's own config). Four promotion
       * helpers read it; without it every percentage falls back to the price
       * mask and a 10% discount renders as `0.10`.
       */
      promotionPercentageFormat: z.string().optional(),
      orderMinAmount: z.number().min(0).default(0),
      /**
       * Storefront-wide. This is *not* cinnamon's per-customer hide-prices
       * rule, which lives on the session as `{ customField, flagValue }` and is
       * derived into `config.options.hidePricesActive` by core's config slice.
       * A page that needs both hides prices when either is true.
       */
      hidePrices: z.boolean().default(false),
      /**
       * The per-customer rule, carried so the storefront-wide switch above is
       * not the only way to hide a price. A storefront names the customer
       * custom field and the value that marks a price-hidden customer; core's
       * config slice resolves it against the session's customer into
       * `config.options.hidePricesActive`.
       *
       * Optional because most storefronts do not use it, and `enabled: false`
       * without the other two fields is a valid "off", not a misconfiguration.
       */
      hidePricesRule: z
        .object({
          enabled: z.boolean().default(false),
          customField: z.string().optional(),
          flagValue: z.union([z.string(), z.number(), z.boolean()]).optional(),
        })
        .optional(),
      placeholderImage: z.string().default(''),
      counter: z
        .object({
          step: z.number().positive().default(1),
          max: z.number().positive().nullable().default(null),
        })
        .default({}),
    })
    .default({}),
  /**
   * Namespaced copy: `{ [namespace]: { [key]: string } }`, structurally equal to
   * core's `ITextLibrary`. Two levels, not one — the ported promotion helpers
   * read `config.texts.promotions.someKey` directly, so a flat
   * `Record<string, string>` could not feed them.
   */
  texts: z.record(z.string(), z.record(z.string(), z.string())).default({}),
  metatags: z
    .object({
      title: z.string().default(''),
      description: z.string().default(''),
      favicon: z.string().default(''),
    })
    .default({}),
})

export type StorefrontConfig = z.infer<typeof storefrontConfigSchema>

export class ConfigValidationError extends Error {
  constructor(readonly issues: z.ZodIssue[]) {
    const summary = issues
      .map((i) => `${i.path.join('.') || '(root)'}: ${i.message}`)
      .join('; ')
    super(`Invalid storefront config — ${summary}`)
    this.name = 'ConfigValidationError'
  }
}

/**
 * Parse, don't validate. commerce-scs runs with FORCE_VALIDATION=false in
 * every environment, so this is the only real gate on config shape.
 */
export const parseStorefrontConfig = (raw: unknown): StorefrontConfig => {
  const result = storefrontConfigSchema.safeParse(raw)
  if (!result.success) throw new ConfigValidationError(result.error.issues)
  return result.data
}
